
import React, { useEffect, useRef, useState } from 'react';
import { FaTrash } from 'react-icons/fa';
import './procedure.css';
import { useAuth } from "../../AuthContext";
import { useNavigate } from "react-router-dom";
import AxiosInstance from "../../AxiosInstance";

export default function App() {
  const [serviceSearch, setServiceSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [editingStepId, setEditingStepId] = useState(null);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [steps, setSteps] = useState([]);
  const [isLoadingSteps, setIsLoadingSteps] = useState(false);
  const [stepForm, setStepForm] = useState({
    titre: '', description: '', horaires: '', cout: '', lieu: '', statut: 'en_attente'
  });
  const [docForm, setDocForm] = useState({
    nom: '', description: '', obligatoire: false, etape_id: ''
  });
  const [docFile, setDocFile] = useState(null);
  const [message, setMessage] = useState(null);
  const filteredServices = services.filter(service =>
    service.title.toLowerCase().includes(serviceSearch.toLowerCase())
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(() => () => { });
  const [confirmMessage, setConfirmMessage] = useState('');
  const openConfirmModal = (message, onConfirm) => {
    setConfirmMessage(message);
    setConfirmAction(() => onConfirm);
    setShowConfirmModal(true);
  };



  useEffect(() => {
    AxiosInstance.get('/api/services')
      .then(res => setServices(res.data))
      .catch(() => showMessage('Verifier votre connexion internet', 'error'));
  }, []);

  useEffect(() => {
    if (selectedServiceId) fetchSteps();
    else setSteps([]);
  }, [selectedServiceId]);

  const fetchSteps = async () => {
    setIsLoadingSteps(true);
    try {
      const res = await AxiosInstance.get(`/api/etapes/service/${selectedServiceId}`);
      const result = res.data;
      const stepsData = result.data || [];
      for (const step of stepsData) {
        const docRes = await AxiosInstance.get(`/api/documents/etape/${step._id}`);
        const docJson = docRes.data;
        step.documents = docJson.data || [];
      }
      setSteps(stepsData);
    } catch {
      showMessage('Erreur lors du chargement des étapes', 'error');
    } finally {
      setIsLoadingSteps(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleStepSubmit = async (e) => {
    e.preventDefault();
    if (!selectedServiceId) return showMessage("Sélectionnez une procédure d'abord", 'error');

    const data = {
      ...stepForm,
      service_id: selectedServiceId,
      cout: parseInt(stepForm.cout || '0', 10) || steps.length + 1
    };

    try {
      if (editingStepId) {
        await AxiosInstance.put(`/api/etapes/${editingStepId}`, data);
      } else {
        await AxiosInstance.post('/api/etapes', data);
      }

      showMessage(editingStepId ? "Étape mise à jour avec succès!" : "Étape créée avec succès!");

      // Réinitialiser
      setStepForm({ titre: '', description: '', horaires: '', cout: '', lieu: '', statut: 'en_attente' });
      setEditingStepId(null);
      fetchSteps();
    } catch {
      showMessage("Erreur lors de la sauvegarde de l'étape", 'error');
    }
  };

  const handleDocSubmit = async (e) => {
    e.preventDefault();
    if (!docForm.etape_id || !docForm.nom) return showMessage("Champs obligatoires manquants", 'error');
    const formData = new FormData();
    for (const key in docForm) formData.append(key, docForm[key]);
    if (docFile) formData.append('modele', docFile);
    try {
      await AxiosInstance.post('/api/documents', formData);
      showMessage("Document ajouté avec succès!");
      setDocForm({ nom: '', description: '', obligatoire: false, etape_id: '' });
      setDocFile(null);
      fetchSteps();
    } catch {
      showMessage("Erreur lors de l'ajout du document", 'error');
    }
  };

  const deleteStep = async (id) => {
    openConfirmModal("Voulez vous supprimer cette étape ?", async () => {
      try {
        await AxiosInstance.delete(`/api/etapes/${id}`);
        showMessage("Étape supprimée");
        fetchSteps();
      } catch {
        showMessage("Erreur de suppression", 'error');
      } finally {
        setShowConfirmModal(false);
      }
    });
  };

  const deleteDocument = async (docId) => {
    openConfirmModal("Voulez vous supprimer ce document ?", async () => {
      try {
        await AxiosInstance.delete(`/api/documents/${docId}`);
        showMessage("Document supprimé");
        fetchSteps();
      } catch {
        showMessage("Erreur de suppression", 'error');
      } finally {
        setShowConfirmModal(false);
      }
    });
  };



  const handleCancelEdit = () => {
    setStepForm({ titre: '', description: '', horaires: '', cout: '', lieu: '', statut: 'en_attente' });
    setEditingStepId(null);
  };

  // Trouver le service sélectionné
  const selectedService = services.find(s => s._id === selectedServiceId);


  // Gérer clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
        setServiceSearch(''); // Vider le champ lorsqu'on clique à l'extérieur
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const containerRef = useRef(null);

  // Gérer clic en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/"); // redirige si pas connecté
    }
  }, [isAuthenticated, navigate]);

  if (!user) return null;

  return (
    <div className="container">
      <div className="header">
        <h1>🔧 Gestionnaire d'étapes de Procedures</h1>
        <p>Créez et gérez les étapes et documents de vos procédures</p>

        {message && (
          <div className="custom-modal-overlay">
            <div className="custom-modal">
              <p>✔️ {message.text}</p>
            </div>
          </div>
        )}


      </div>


      <div className="service-selector" ref={containerRef} style={{ position: 'relative' }}>
        <input
          type="text"
          className="form-control"
          placeholder="Selectionner un service / 🔍 Rechercher un service"
          value={serviceSearch}
          onFocus={() => setShowDropdown(true)}
          onChange={(e) => {
            setServiceSearch(e.target.value);
            setShowDropdown(true);
          }}
          style={{ marginBottom: '10px' }}
        />

        {showDropdown && (
          <div className="dropdown-list" style={{
            border: '1px solid #ccc',
            borderRadius: '4px',
            maxHeight: '200px',
            overflowY: 'auto',
            background: '#fff',
            zIndex: 999,
            position: 'absolute',
            width: '95%',
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
          }}>
            {filteredServices.length > 0 ? (
              filteredServices.map(service => (
                <div
                  key={service._id}
                  className="dropdown-item"
                  style={{ padding: '8px 12px', cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedServiceId(service._id);
                    setServiceSearch('');
                    setShowDropdown(false);
                  }}
                >
                  {service.title}
                </div>
              ))
            ) : (
              <div style={{ padding: '8px 12px', color: '#888' }}>
                ❌ Ce service n'est pas disponible
              </div>
            )}
          </div>
        )}

        {/* Affichage du nom du service sélectionné */}
        {selectedService && (
          <div style={{
            marginTop: '10px',
            padding: '8px 12px',
            backgroundColor: '#f2f2f2',
            borderRadius: '4px',
            fontWeight: 'bold',
            color: '#333'
          }}>
            ✅ Service sélectionné : {selectedService.title}
          </div>
        )}
      </div>

      <div className="main-content">
        <div className="steps-section">
          <h2>📋 Étapes de la procédure sélectionnée</h2>
          {isLoadingSteps ? (
            <p>Chargement...</p>
          ) : steps.length === 0 ? (
            <p>Aucune étape trouvée. Créez la première étape!</p>
          ) : (
            steps.map((step, i) => (
              <div key={step._id} className="step-card">
                <div className="step-header">
                  <div className="step-info">
                    <h5>{i + 1}. {step.titre || `Étape ${i + 1}`}</h5>
                    <div className="step-description">{step.description || 'Aucune description'}</div>
                  </div>
                  <div className="step-actions">
                    <button className="btn btn-danger btn-small" onClick={() => deleteStep(step._id)}>
                      <FaTrash style={{ marginRight: 6 }} />
                      Supprimer
                    </button>
                    <button
                      className="btn btn-warning btn-small"
                      style={{ marginLeft: '10px' }}
                      onClick={() => {
                        setStepForm({
                          titre: step.titre || '',
                          description: step.description || '',
                          horaires: step.horaires || '',
                          cout: step.cout || '',
                          lieu: step.lieu || '',
                          statut: step.statut || 'en_attente'
                        });
                        setEditingStepId(step._id);
                        window.scrollTo({ top: 0, behavior: 'smooth' }); // scroll en haut pour voir le formulaire
                      }}
                    >
                      📝 Modifier
                    </button>

                  </div>
                </div>
                <div className="step-content" style={{ lineHeight: '1.0' }}>
                  {step.instructions && <p className="step-line"><strong>Instructions :</strong> {step.instructions}</p>}
                  {step.lieu && <p className="step-line"><strong>Lieu :</strong> {step.lieu}</p>}
                  {step.horaires && <p className="step-line"><strong>Horaires estimées :</strong> {step.horaires}</p>}
                  {step.statut && <p className="step-line"><strong>Statut :</strong> {step.statut}</p>}

                  <h5 style={{ marginTop: '5px' }}>📄 Documents :</h5>

                  {step.documents?.length > 0 ? (
                    <div className="documents-list">
                      {step.documents.map(doc => (
                        <div
                          key={doc._id}
                          className={`document-item ${doc.obligatoire ? 'required' : 'optional'}`}
                          style={{ marginBottom: '1px', padding: '8px', border: '1px solid #eee', borderRadius: '4px' }}
                        >
                          <div className="document-info" style={{ marginBottom: '4px' }}>
                            <div className="document-name" style={{ fontWeight: 'bold' }}>{doc.nom}</div>
                            <div className="document-description" style={{ fontSize: '0.9em', color: '#666' }}>
                              {doc.description || 'Aucune description'}
                            </div>
                          </div>

                          <div className="step-actions" style={{ display: 'flex', gap: '8px' }}>
                            <div
                              className={`document-badge ${doc.obligatoire ? 'required' : 'optional'}`}
                              style={{
                                minWidth: '100px',
                                height: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: doc.obligatoire ? '#dc3545' : '#ffc107',
                                color: doc.obligatoire ? 'white' : 'white',
                                borderRadius: '14px',
                                fontSize: '0.85em'
                              }}
                            >
                              {doc.obligatoire ? 'Obligatoire' : 'Optionnel'}
                            </div>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => deleteDocument(doc._id)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0 8px',
                                height: '30px'
                              }}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-steps" style={{ fontSize: '0.9em', color: '#999', marginTop: '1px' }}>
                      Aucun document associé à cette étape.
                    </p>
                  )}
                </div>

              </div>
            ))
          )}
        </div>

        <div className="forms-section">
          {/* Formulaire Étape */}
          <div className="form-section">
            <h4>{editingStepId ? "📝 Modifier l'étape" : "➕ Créer une nouvelle étape"}</h4>
            <form onSubmit={handleStepSubmit}>
              <div className="form-group">
                <label className="form-label">Nom de l'étape *</label>
                <input type="text" className="form-control" value={stepForm.titre} onChange={e => setStepForm({ ...stepForm, titre: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows="3" value={stepForm.description} onChange={e => setStepForm({ ...stepForm, description: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Lieu</label>
                <input type="text" className="form-control" placeholder="Mairie de NEW BELL" value={stepForm.lieu} onChange={e => setStepForm({ ...stepForm, lieu: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Horaires estimées</label>
                <input type="text" className="form-control" placeholder="Ex: De 8h à 16h, les jours ouvrables" value={stepForm.horaires} onChange={e => setStepForm({ ...stepForm, horaires: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Coût estimé</label>
                <input type="number" className="form-control" min="0" value={stepForm.cout} onChange={e => setStepForm({ ...stepForm, cout: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Statut</label>
                <select className="form-control" value={stepForm.statut} onChange={e => setStepForm({ ...stepForm, statut: e.target.value })}>
                  <option value="en_attente">En attente</option>
                  <option value="en_cours">En cours</option>
                  <option value="terminé">Terminé</option>
                  <option value="bloqué">Bloqué</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '5px' }}>
                <button
                  type="submit"
                  className="bta btn-success"
                  style={{ flex: 3 }}
                >
                  💾 {editingStepId ? "Enregistrer les modifications" : "Créer l'étape"}
                </button>

                {editingStepId && (
                  <button
                    type="button"
                    className="bta btn-secondary"
                    style={{ flex: 1 }}
                    onClick={handleCancelEdit}
                  >
                    🚫 Annuler
                  </button>
                )}
              </div>


            </form>
          </div>

          {/* Formulaire Document */}
          <div className="form-section">
            <h4>📄 Ajouter un document à une étape</h4>
            <form onSubmit={handleDocSubmit}>
              <div className="form-group">
                <label className="form-label">Étape *</label>
                <select className="form-control" value={docForm.etape_id} onChange={e => setDocForm({ ...docForm, etape_id: e.target.value })} required>
                  <option value="">Sélectionnez une étape</option>
                  {steps.map(step => (
                    <option key={step._id} value={step._id}>{step.titre}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Nom du document *</label>
                <input type="text" className="form-control" value={docForm.nom} onChange={e => setDocForm({ ...docForm, nom: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows="3" value={docForm.description} onChange={e => setDocForm({ ...docForm, description: e.target.value })} />
              </div>
              <div className="form-group">
                <div className="checkbox-group">
                  <input type="checkbox" id="documentRequired" checked={docForm.obligatoire} onChange={e => setDocForm({ ...docForm, obligatoire: e.target.checked })} />
                  <label htmlFor="documentRequired">Document obligatoire</label>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Modèle de document</label>
                <div className="file-upload">
                  <input
                    type="file"
                    id="documentFile"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                    onChange={e => {
                      const file = e.target.files[0];
                      if (file) setDocFile(file);
                    }}
                  />
                  <label className="file-upload-label" htmlFor="documentFile">
                    {docFile ? `📁 ${docFile.name}` : '📁 Cliquez pour sélectionner un fichier modèle'}
                  </label>
                </div>
              </div>
              <button type="submit" className="bta btn-success" style={{ width: '100%' }}>
                💾 Ajouter le document
              </button>
            </form>
          </div>
        </div>
      </div>
      {showConfirmModal && (
        <div className="custom-modal-overlay">
          <div className="custom-modal">
            <p>{confirmMessage}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '15px' }}>
              <button className="btn btn-danger" onClick={() => setShowConfirmModal(false)}>NON</button>
              <button className="btn btn-secondary" onClick={confirmAction}>OUI</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
